/*
 * This class is copied verbatim from Backstage 1.39.1.
 */

import { InfoCard, UserIdentity } from '@mainstage/core-components';
import { errorApiRef, useApi } from '@mainstage/core-plugin-api';
import { useTranslationRef } from '@mainstage/core-plugin-api/alpha';
import { ForwardedError } from '@mainstage/errors';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import {
  coreComponentsTranslationRef,
  LICENSE_EXPIRED_LOGIN_MESSAGE,
} from '../translation';
import { GridItem } from './styles';
import {
  ProviderComponent,
  ProviderLoader,
  SignInProvider,
  SignInProviderConfig,
} from './types';

const Component: ProviderComponent = ({
  config,
  onSignInStarted,
  onSignInSuccess,
  onSignInFailure,
}) => {
  const { apiRef, title, message } = config as SignInProviderConfig;
  const authApi = useApi(apiRef);
  const errorApi = useApi(errorApiRef);
  const { t } = useTranslationRef(coreComponentsTranslationRef);

  const handleLogin = async () => {
    try {
      onSignInStarted();
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!identityResponse) {
        onSignInFailure();
        throw new Error(
          `The ${title} provider is not configured to support sign-in`,
        );
      }

      const profile = await authApi.getProfile();

      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          profile,
          authApi,
        }),
      );
    } catch (error) {
      onSignInFailure();
      if (
        (error as any)?.status === 402 ||
        (error as any)?.message?.includes('402')
      ) {
        errorApi.post(new Error(LICENSE_EXPIRED_LOGIN_MESSAGE));
      } else {
        errorApi.post(new ForwardedError(t('signIn.loginFailed'), error));
      }
    }
  };

  return (
    <GridItem>
      <InfoCard
        variant="fullHeight"
        title={title}
        actions={
          <Button color="primary" variant="outlined" onClick={handleLogin}>
            {t('signIn.title')}
          </Button>
        }
      >
        <Typography variant="body1">{message}</Typography>
      </InfoCard>
    </GridItem>
  );
};

const loader: ProviderLoader = async (apis, apiRef) => {
  const authApi = apis.get(apiRef)!;

  const identityResponse = await authApi.getBackstageIdentity({
    optional: true,
  });

  if (!identityResponse) {
    return undefined;
  }

  const profile = await authApi.getProfile();

  return UserIdentity.create({
    identity: identityResponse.identity,
    profile,
    authApi,
  });
};

export const commonProvider: SignInProvider = { Component, loader };
